import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Card, Form, Input, Select, Space, message, Modal, Switch } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  CopyOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { Service } from '@/apis';
import type { ProductAddDto, ProductUpdateDto } from '@/apis';
import RichTextEditor from '@/components/RichTextEditor';
import {
  toStrList,
  toSpecList,
  MultiImageUpload,
  type ProductSpec,
  type ProductRow,
} from './productShared';
import styles from '@/components/CommonTable/index.module.less';
import formStyles from './ProductForm.module.less';

const PRODUCT_CLIPBOARD_TYPE = 'sinolinkway_product';

function isProductClipboardData(obj: unknown): obj is Record<string, unknown> {
  return (
    obj != null &&
    typeof obj === 'object' &&
    (obj as Record<string, unknown>).__type === PRODUCT_CLIPBOARD_TYPE
  );
}

function buildProductClipboardData(values: Record<string, unknown>): string {
  return JSON.stringify({ __type: PRODUCT_CLIPBOARD_TYPE, ...values });
}

function tryParseProductClipboard(
  text: string
): Record<string, unknown> | null {
  try {
    const obj = JSON.parse(text);
    if (!isProductClipboardData(obj)) return null;
    const rest = Object.fromEntries(
      Object.entries(obj).filter(([k]) => k !== '__type')
    ) as Record<string, unknown>;
    if (typeof rest.name !== 'string' && rest.name != null) return null;
    return rest as Record<string, unknown>;
  } catch {
    return null;
  }
}

const DRAFT_KEY_PREFIX = 'product_draft_';

function getDraftKey(id: string | undefined): string {
  return DRAFT_KEY_PREFIX + (id || 'new');
}

function loadDraft(key: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

function saveDraft(key: string, values: Record<string, unknown>): void {
  try {
    localStorage.setItem(key, JSON.stringify(values));
  } catch (e) {
    console.warn('草稿保存失败', e);
  }
}

function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

const ProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const draftKey = getDraftKey(id);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [canLoadDetail, setCanLoadDetail] = useState(!isEdit);
  const [categories, setCategories] = useState<
    { key?: string; title?: string }[]
  >([]);
  const [dirty, setDirty] = useState(false);
  const [clipboardHasProductData, setClipboardHasProductData] = useState(false);
  const saveDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCheckedDraftRef = useRef(false);
  const hasCheckedClipboardRef = useRef(false);

  useEffect(() => {
    Service.ycCategoryControllerGet(1, 500)
      .then((res: unknown) => {
        const data = (res as { data?: { rows?: unknown[] } })?.data;
        const rows = (data?.rows ?? []) as Record<string, unknown>[];
        setCategories(
          rows.map(r => {
            const d = (r.data as Record<string, unknown>) || r;
            return {
              key: (d.key as string) || (r.key as string),
              title:
                (d.title as string) ||
                (r.title as string) ||
                (d.key as string) ||
                (r.key as string),
            };
          })
        );
      })
      .catch(() => {});
  }, []);

  const persistDraft = useCallback(() => {
    const values = form.getFieldsValue();
    saveDraft(draftKey, values);
  }, [draftKey, form]);

  const scheduleDraftSave = useCallback(() => {
    setDirty(true);
    if (saveDraftTimerRef.current) clearTimeout(saveDraftTimerRef.current);
    saveDraftTimerRef.current = setTimeout(() => {
      persistDraft();
      saveDraftTimerRef.current = null;
    }, 400);
  }, [persistDraft]);

  const normalizeDraft = useCallback(
    (draft: Record<string, unknown>): Record<string, unknown> => {
      const normalized = { ...draft };
      if (!Array.isArray(normalized.specs) || normalized.specs.length === 0) {
        normalized.specs = [];
      } else {
        normalized.specs = (
          normalized.specs as Array<{ label?: string; value?: string }>
        ).map(s => ({
          label: s?.label ?? '',
          value: s?.value ?? '',
        }));
      }
      return normalized;
    },
    []
  );

  const applyDraft = useCallback(
    (draft: Record<string, unknown>) => {
      form.setFieldsValue(normalizeDraft(draft));
      setDirty(false);
    },
    [form, normalizeDraft]
  );

  const clearClipboardAndHidePaste = useCallback(() => {
    setClipboardHasProductData(false);
    navigator.clipboard?.writeText?.('').catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit || !id || hasCheckedDraftRef.current) return;
    hasCheckedDraftRef.current = true;
    const draft = loadDraft(draftKey);
    if (draft) {
      Modal.confirm({
        title: '恢复未保存内容',
        content: '检测到本地有该产品的未保存编辑内容，是否恢复？',
        okText: '恢复',
        cancelText: '放弃',
        onOk: () => applyDraft(draft),
        onCancel: () => {
          clearDraft(draftKey);
          setCanLoadDetail(true);
        },
      });
    } else {
      setCanLoadDetail(true);
    }
  }, [isEdit, id, draftKey, applyDraft]);

  useEffect(() => {
    if (hasCheckedClipboardRef.current) return;
    hasCheckedClipboardRef.current = true;
    navigator.clipboard
      ?.readText?.()
      .then(text => {
        const data = tryParseProductClipboard(text);
        if (!data) return;
        setClipboardHasProductData(true);
        if (!isEdit) {
          Modal.confirm({
            title: '粘贴产品数据',
            content: '检测到剪贴板有产品数据，是否粘贴？',
            okText: '粘贴',
            cancelText: '取消',
            onOk: () => {
              applyDraft(data);
              clearClipboardAndHidePaste();
            },
          });
        }
      })
      .catch(() => {});
  }, [isEdit, applyDraft, clearClipboardAndHidePaste]);

  const handlePaste = useCallback(() => {
    navigator.clipboard
      ?.readText?.()
      .then(text => {
        const data = tryParseProductClipboard(text);
        if (!data) {
          message.info('剪贴板无有效产品数据');
          setClipboardHasProductData(false);
          return;
        }
        const doPaste = () => {
          applyDraft(data);
          clearClipboardAndHidePaste();
        };
        if (isEdit) {
          Modal.confirm({
            title: '粘贴产品数据',
            content: '粘贴将覆盖当前表单内容，是否继续？',
            okText: '粘贴',
            cancelText: '取消',
            onOk: doPaste,
          });
        } else {
          doPaste();
        }
      })
      .catch(() => message.error('读取剪贴板失败'));
  }, [isEdit, applyDraft, clearClipboardAndHidePaste]);

  const handleCopy = useCallback(() => {
    const values = form.getFieldsValue();
    const str = buildProductClipboardData(values);
    navigator.clipboard
      ?.writeText?.(str)
      .then(() => {
        message.success('已复制到剪贴板');
      })
      .catch(() => {
        message.error('复制失败');
      });
  }, [form]);

  useEffect(() => {
    if (!isEdit || !id || !canLoadDetail) return;
    setDetailLoading(true);
    Service.ycProductControllerGetDetail(id)
      .then((res: unknown) => {
        const raw = (res as { data?: unknown })?.data as
          | Record<string, unknown>
          | undefined;
        const data = (raw?.data as Record<string, unknown>) || raw;
        if (!data) return;
        const row: ProductRow = {
          ...data,
          id: (data.id as number) ?? Number(id),
        } as ProductRow;
        form.setFieldsValue({
          ...row,
          image: toStrList(row.image),
          images: toStrList(row.images),
          specs:
            toSpecList(row.specs).length > 0 ? toSpecList(row.specs) : [],
          isFeatured: row.isFeatured === 1,
          status: row.status || 'published',
        });
        setDirty(false);
      })
      .catch(() => message.error('获取产品详情失败'))
      .finally(() => setDetailLoading(false));
  }, [id, isEdit, form, canLoadDetail]);

  const onFinish = async (values: Record<string, unknown>) => {
    const image = toStrList(values.image as string | string[] | undefined);
    const images = toStrList(values.images as string | string[] | undefined);
    const specs = ((values.specs as ProductSpec[] | undefined) ?? []).filter(
      s => (s?.label?.trim() ?? '') !== '' && (s?.value?.trim() ?? '') !== ''
    );

    setLoading(true);
    try {
      if (isEdit && id) {
        const payload: ProductUpdateDto = {
          id: Number(id),
          name: values.name as string,
          category: values.category as string,
          summary: values.summary as string,
          description: values.description as string,
          content: values.content as string,
          price: values.price as string,
          image: image.length > 0 ? image : undefined,
          images: images.length > 0 ? images : undefined,
          specs:
            specs.length > 0 ? (specs as unknown as Array<string>) : undefined,
          isFeatured: values.isFeatured ? 1 : 0,
          status: (values.status as string) || 'published',
        };
        await Service.ycProductControllerPut(payload);
        message.success('更新成功');
      } else {
        const payload: ProductAddDto = {
          name: values.name as string,
          category: values.category as string,
          summary: values.summary as string,
          description: values.description as string,
          content: values.content as string,
          price: values.price as string,
          image: image.length > 0 ? image : undefined,
          images: images.length > 0 ? images : undefined,
          specs:
            specs.length > 0 ? (specs as unknown as Array<string>) : undefined,
          isFeatured: values.isFeatured ? 1 : 0,
          status: (values.status as string) || 'published',
        };
        await Service.ycProductControllerAdd(payload);
        message.success('创建成功');
      }
      clearDraft(draftKey);
      setDirty(false);
      navigate('/cms/product');
    } catch {
      message.error(isEdit ? '更新失败' : '创建失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const goBack = useCallback(() => {
    if (dirty) {
      Modal.confirm({
        title: '未保存的更改',
        content: '当前有未保存的更改，确定要离开吗？',
        okText: '确定离开',
        cancelText: '取消',
        onOk: () => {
          navigate('/cms/product');
        },
      });
    } else {
      navigate('/cms/product');
    }
  }, [dirty, navigate]);

  return (
    <div className={styles.dataTablePage}>
      <div className={formStyles.productFormHeader}>
        <div className={formStyles.productFormHeaderLeft}>
          <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
            返回
          </Button>
          <span className={formStyles.productFormTitle}>
            {isEdit ? '编辑产品' : '新增产品'}
          </span>
        </div>
        <Space>
          {clipboardHasProductData && (
            <Button icon={<SnippetsOutlined />} onClick={handlePaste}>
              粘贴
            </Button>
          )}
          <Button type='primary' icon={<CopyOutlined />} onClick={handleCopy}>
            复制
          </Button>
          <Button
            type='primary'
            loading={loading}
            onClick={() => form.submit()}
          >
            {isEdit ? '保存' : '创建'}
          </Button>
          <Button onClick={goBack}>取消</Button>
        </Space>
      </div>
      <div className={formStyles.cardWrap}>
        <Card>
          <Form
            form={form}
            layout='vertical'
            initialValues={{ specs: [], isFeatured: false, status: 'published' }}
            onFinish={onFinish}
            onValuesChange={scheduleDraftSave}
            disabled={detailLoading}
            className={formStyles.productForm}
          >
            <div className={formStyles.formRow2col}>
              <Form.Item
                name='name'
                label='名称'
                rules={[{ required: true, message: '请输入名称' }]}
              >
                <Input placeholder='产品名称' />
              </Form.Item>
              <Form.Item
                name='summary'
                label='摘要'
                rules={[{ required: false, message: '请输入摘要' }]}
              >
                <Input.TextArea rows={2} placeholder='简短描述' />
              </Form.Item>
            </div>
            <div className={formStyles.formRow2col}>
              <Form.Item
                name='category'
                label='分类'
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Select
                  placeholder='选择分类'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  options={categories.map(c => ({
                    value: c.key,
                    label: c.title || c.key,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name='price'
                label='价格'
                rules={[{ required: true, message: '请输入价格' }]}
              >
                <Input placeholder='如 99.00' />
              </Form.Item>
            </div>
            <div className={formStyles.formRow2col}>
              <Form.Item
                name='isFeatured'
                label='精选产品'
                extra='勾选后展示在首页 FEATURED PRODUCTS'
                valuePropName='checked'
              >
                <Switch checkedChildren='是' unCheckedChildren='否' />
              </Form.Item>
              <Form.Item
                name='status'
                label='状态'
                extra='草稿和下架的产品不会在前台展示'
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select
                  options={[
                    { value: 'published', label: '已上架' },
                    { value: 'draft', label: '草稿' },
                    { value: 'offline', label: '下架' },
                  ]}
                />
              </Form.Item>
            </div>
            <div className={formStyles.formItemFull}>
              <Form.Item
                name='image'
                label='主图'
                extra='建议 1:1 比例，推荐尺寸 800×800 或 1000×1000 像素；不限制尺寸，支持多张，展示时取首张为主图'
                required
                rules={[
                  {
                    validator: (_, value) => {
                      const arr = Array.isArray(value)
                        ? value
                        : value
                          ? [value]
                          : [];
                      if (
                        arr.filter((v: string) => v && String(v).trim())
                          .length < 1
                      ) {
                        return Promise.reject(new Error('请至少上传一张主图'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <MultiImageUpload />
              </Form.Item>
            </div>
            <div className={formStyles.formItemFull}>
              <Form.Item name='description' label='详情（简短）'>
                <Input.TextArea rows={2} placeholder='用于列表等简短说明' />
              </Form.Item>
            </div>
            <div className={formStyles.formItemFull}>
              <Form.Item
                label='规格'
                extra='如：颜色、尺寸等，每项为 label + value'
              >
                <Form.List name='specs'>
                  {(fields, { add, remove }) => (
                    <div className={formStyles.specsGrid}>
                      {fields.map(({ key, name, ...rest }) => (
                        <div key={key} className={formStyles.specItem}>
                          <Form.Item
                            {...rest}
                            name={[name, 'label']}
                            rules={[{ required: true, message: '名称' }]}
                          >
                            <Input placeholder='名称（如 颜色）' />
                          </Form.Item>
                          <Form.Item
                            {...rest}
                            name={[name, 'value']}
                            rules={[{ required: true, message: '值' }]}
                          >
                            <Input placeholder='值（如 红色）' />
                          </Form.Item>
                          <Button
                            type='text'
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        </div>
                      ))}
                      <div className={formStyles.specAddBtn}>
                        <Button
                          type='dashed'
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                        >
                          添加规格
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.List>
              </Form.Item>
            </div>
            <div className={formStyles.formItemFull}>
              <Form.Item
                name='images'
                label='详情图'
                extra='建议 1:1 或 4:3，推荐尺寸 800×800 或 1200×900 像素；不限制尺寸；详情页优先展示本组图片，再展示下方编辑内容'
              >
                <MultiImageUpload maxCount={20} />
              </Form.Item>
            </div>
            <div className={formStyles.formItemFull}>
              <Form.Item name='content' label='编辑内容（富文本）'>
                <RichTextEditor
                  placeholder='产品详情富文本，在详情图中之后展示'
                  minHeight={300}
                  autoHeight
                />
              </Form.Item>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default ProductForm;
